import { useEffect, useMemo } from "react";

import { Flex } from "@chakra-ui/react";
import { datadogRum } from "@datadog/browser-rum";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { useSharedListClaimerName } from "flows/Inbound/hooks/useSharedListClaimerName";
import { useSharedListClaimers } from "flows/Inbound/hooks/useSharedListClaimers";
import { useFetchClaimedProducts } from "flows/Inbound/stores/useInboundDropService";
import { GroupUserIcon } from "ui/Icons/Icons";
import { DetailL } from "ui/Typography/Typography";

import { useInboundStore } from "../stores/inboundStore/useInboundStore";

const SHOW_EMPLOYEE_NAME_THRESHOLD = 2;

export function SharedListClaimers() {
  const intl = useIntl();
  const { droppingListId } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
    })),
  );

  const {
    refreshClaimedProducts,
    isPollingStopped: isRefreshClaimedProductsPollingStopped,
    claimerIds: refreshedClaimerIds,
  } = useFetchClaimedProducts();

  const {
    claimerIds,
    error,
    refreshSharedListClaimers,
    isPollingStopped: isSharedListClaimersPollingStopped,
  } = useSharedListClaimers({
    listId: droppingListId ?? "",
    shouldSkip: !droppingListId,
    onClaimersChange: () => {
      refreshClaimedProducts();
    },
  });

  if (error) {
    datadogRum.addError("Error fetching inbounding list claimers:", { error });
  }

  // When polling for getProductForClaimedListSubset, use the refreshed claimerIds for up-to-date claimerIds otherwise use the original claimerIds
  const upToDateClaimerIds = useMemo(() => {
    if (isSharedListClaimersPollingStopped) {
      return refreshedClaimerIds;
    }
    return claimerIds;
  }, [isSharedListClaimersPollingStopped, refreshedClaimerIds, claimerIds]);

  const shouldShowEmployeeName = useMemo(
    () => upToDateClaimerIds.length === SHOW_EMPLOYEE_NAME_THRESHOLD,
    [upToDateClaimerIds],
  );

  const { badgeNo } = useEmployeeStore(
    useShallow((state) => ({
      badgeNo: state.badgeNo,
    })),
  );

  const employeeName = useSharedListClaimerName({
    badgeNo,
    claimerIds: upToDateClaimerIds,
    skip: !shouldShowEmployeeName,
  });

  const visibleClaimersCount = useMemo(() => upToDateClaimerIds.length - 1, [upToDateClaimerIds]);
  const summary = useMemo(
    () =>
      upToDateClaimerIds.length === SHOW_EMPLOYEE_NAME_THRESHOLD
        ? intl.formatMessage(
            {
              id: "flows.inbound.components.shared-list-component.claimed-list",
            },
            { name: employeeName },
          )
        : intl.formatMessage(
            {
              id: "flows.inbound.components.shared-list-component.people-claimed-list",
            },
            { claimers: visibleClaimersCount },
          ),
    [employeeName, intl, upToDateClaimerIds.length, visibleClaimersCount],
  );

  useEffect(() => {
    if (isRefreshClaimedProductsPollingStopped) {
      refreshSharedListClaimers();
    }
  }, [isRefreshClaimedProductsPollingStopped, refreshSharedListClaimers]);

  if (visibleClaimersCount <= 0) {
    return null;
  }

  return (
    <Flex alignItems="center" gap={2}>
      <GroupUserIcon fill="none" />
      <DetailL>{summary}</DetailL>
    </Flex>
  );
}
